/*
 * W. R. Long product-page fitment check.
 *
 * Answers one question on a product page: which tractors does this part fit? It reads the current
 * variant's SKU, looks it up in `wrl-fit-pdp.js`, and lists the models - each one a link into the
 * finder, pre-narrowed to that tractor.
 *
 * Deliberately a separate file from `wrl-fit.js` rather than another custom element inside it.
 * The finder's bundle carries a type-ahead combobox, a four-step narrowing UI and live product
 * fetching, none of which a product page uses; this is the highest-traffic template on the store,
 * so it loads ~4 KB of its own instead of ~9 KB gz of someone else's. The two share no runtime, on
 * purpose - the only thing they must agree on is the SKU rule below.
 */
(function () {
  "use strict";

  /* Must match `baseSku(normalizeSku(...))` in lib/fitment/core.ts, which is what keyed the index.
     Two directions need it. Shopify sells the flat-face coupler as a second variant whose SKU ends
     `+HQFF` (384 of them) and the catalogue has never heard of those; and the catalogue itself
     carries part numbers ending in a bare `+` (`VKJD520M-3C18+`). Both sides strip at the first
     `+`, so both meet in the middle. If these two rules ever drift apart the failure is silent -
     the page simply says nothing - so they are asserted by test on the build side. */
  function lookupKey(sku) {
    var s = String(sku || "").trim().toUpperCase();
    var plus = s.indexOf("+");
    return plus === -1 ? s : s.slice(0, plus);
  }

  /* Same slug rule as wrl-fit.js, so the links it builds are the links the finder reads back. */
  function slug(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  /* Must match the `v1` segment of the endpoint path. This widget deliberately does not load the
     core bundle — it needs ~4 KB of its own rather than ~9 KB gz of someone else's — so unlike the
     finder it cannot read the version off the deployed engine, and the path is the binding. */
  /* Same mark as the finder's, redrawn rather than shared: these two files share no runtime by
     design, and ~300 bytes of duplicated path data is a cheaper price than a shared bundle on the
     highest-traffic template on the store. If one changes, change both - they are seen in the same
     session, one after the other. */
  var CHECK_SVG =
    '<svg class="wrl-fit__check" viewBox="0 0 20 20" width="20" height="20" ' +
      'aria-hidden="true" focusable="false">' +
      '<circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" stroke-width="1.5"></circle>' +
      '<path d="M5.9 10.4l2.7 2.7 5.6-5.9" fill="none" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round"></path>' +
    "</svg>";

  var PDP_VERSION = 1;
  var INDEX_KEY = "pdp.v" + PDP_VERSION;
  /* As in wrl-fit.js: a ceiling on local work, while the store's 30s failure cooldown is what bounds
     network traffic. A product page that does not mention fitment is a normal page, but a shopper
     still toggling variants a minute later should get another chance. */
  var MAX_INDEX_FAILURES = 8;

  function loadIndex(url) {
    /* A pre-warmed global still works, so anything that sets it (a debugging session, a future
       inline bootstrap) short-circuits the network exactly as before. */
    if (window.__WRL_FIT_PDP__) return Promise.resolve(window.__WRL_FIT_PDP__);

    /* Not assumed to exist. Document-order `defer` guarantees it on a normally rendered page, but a
       dynamically injected section (theme editor, Section Rendering API) ignores `defer` and runs
       scripts in load order. Raised as a rejection so the caller's catch handles it rather than a
       TypeError escaping and leaving the widget wedged. */
    if (!window.WRLFitStore) {
      return Promise.reject(new Error("wrl-fit-store.js has not loaded"));
    }

    window.WRLFitStore.evictOtherVersions("pdp", INDEX_KEY);

    return window.WRLFitStore.load({
      key: INDEX_KEY,
      url: url,
      version: PDP_VERSION,
      /* A background revalidation changed the data. Re-rendering a fitment list under the buy
         button is safe at any time — there is no selection in progress to disturb. */
      onRefresh: function (payload) {
        mounted.forEach(function (el) {
          if (!el.isConnected) return;
          el.index = payload;
          el.render();
        });
      },
    }).then(function (result) {
      return result.payload;
    });
  }

  /* Mounted elements, so a refreshed payload can reach every instance on the page. Named for what
     it holds rather than `instances`, which is already the id counter below — the collision left it
     as a number and `indexOf` threw out of connectedCallback, so nothing rendered anywhere. */
  var mounted = [];

  /* Ids must be unique per instance, not per product: the snippet can legitimately appear twice on
     one page while a merchant compares the block placement against the section placement, and
     `aria-controls` on both toggles would otherwise point at the first list. */
  var instances = 0;

  class WrlFitCheck extends HTMLElement {
    connectedCallback() {
      // A reconnect re-arms both halves. `listen()` as well as `watch()`: disconnectedCallback
      // removes the variant listener, and a widget that is moved in the DOM would otherwise keep
      // showing the variant that was selected when it was detached - a stale fitment list, which
      // is worse than a slow one.
      /* Before the early return: disconnectedCallback always unregisters, so a widget moved in the
         DOM — the case that branch exists for — would otherwise never receive a refresh again. */
      if (mounted.indexOf(this) === -1) mounted.push(this);
      if (this._booted) { this.watch(); this.listen(); return; }
      this._booted = true;

      this.pageUrl = this.dataset.pageUrl || "/pages/find-your-fit";
      // Above this many models the list collapses behind a toggle. 928 of 1,088 parts fit six or
      // fewer, so most pages print the answer outright and never render a control at all.
      this.inlineMax = parseInt(this.dataset.inlineMax, 10);
      if (!(this.inlineMax > 0)) this.inlineMax = 6;
      this.expanded = false;

      this.variants = this.readVariants();
      this.sku = this.dataset.initialSku || "";
      this.listId = "wrl-fit-check-models-" + (instances += 1);

      this.watch();
      this.listen();
    }

    /* Liquid writes the whole variant->sku map into the page, so switching variants costs no
       network call: Momentum's event carries a variant id, and the id is all we need to look up. */
    readVariants() {
      var node = this.querySelector("script[type='application/json']");
      if (!node) return {};
      try {
        return JSON.parse(node.textContent) || {};
      } catch (err) {
        if (window.console) console.error("[wrl-fit-check] variant map is not valid JSON", err);
        return {};
      }
    }

    watch() {
      if (this._loading || this.index) return;
      var self = this;
      var boot = function () { self.boot(); };

      if ("IntersectionObserver" in window) {
        this._io = new IntersectionObserver(function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) boot();
        }, { rootMargin: "300px" });
        this._io.observe(this);
      } else {
        boot();
      }
    }

    unwatch() {
      if (this._io) { this._io.disconnect(); this._io = null; }
    }

    disconnectedCallback() {
      this.unwatch();
      var at = mounted.indexOf(this);
      if (at !== -1) mounted.splice(at, 1);
      if (this._onVariant) {
        document.removeEventListener("ARMADA:VARIANT:CHANGE", this._onVariant);
        this._onVariant = null;
      }
    }

    /*
     * Momentum, not Dawn. There is no `global.js`, no `<variant-selects>`, no `variant:change` and
     * no publish/subscribe here - Dawn-shaped code compiles fine and then silently never updates.
     * The real contract is `assets/component-product-form.min.js`: a document-level
     * ARMADA:VARIANT:CHANGE carrying `detail.variantId`.
     *
     * That event is the whole contract, deliberately. A hidden-input fallback was tried and removed
     * as dead code: the only `.product-variant-id` in the theme is a disabled input in
     * `snippets/armament-payment-buttons.liquid` that nothing updates, the theme assigns `.value`
     * as a property (which never fires an attribute mutation), and an unscoped `input[name='id']`
     * can match a quick-add form for a different product. A fallback that cannot fire is worse than
     * none - it reads like a safety net while providing nothing.
     *
     * The starting SKU needs no lookup at all: Liquid renders it into `data-initial-sku`.
     */
    listen() {
      if (this._onVariant) return;
      var self = this;
      this._onVariant = function (e) {
        var id = e && e.detail && e.detail.variantId;
        if (id != null) self.setVariant(id);
      };
      document.addEventListener("ARMADA:VARIANT:CHANGE", this._onVariant);
    }

    setVariant(variantId) {
      var sku = this.variants[String(variantId)];
      if (sku == null || sku === this.sku) return;

      // Collapse only when the answer actually changes. A base part and its `+HQFF` flat-face
      // sibling resolve to the same models, so snapping a 20-model list shut as the shopper toggles
      // the coupler would be pure noise.
      var sameAnswer = lookupKey(sku) === lookupKey(this.sku);
      this.sku = sku;
      if (!sameAnswer) this.expanded = false;
      if (this.index && !sameAnswer) this.render();
      /* The index never loaded. A shopper changing variants is a deliberate action and the store
         holds a failure cooldown, so one more attempt here is bounded and often succeeds. */
      if (!this.index && !this._loading && (this._failures || 0) < MAX_INDEX_FAILURES) this.boot();
    }

    boot() {
      if (this._loading) return;
      this._loading = true;
      this.unwatch();

      var self = this;
      loadIndex(this.dataset.indexUrl)
        .then(function (index) {
          if (!index) throw new Error("fitment index did not initialise");
          if (index.v !== PDP_VERSION) {
            // Dropped, or a superseded copy would fail this check on every page view.
            window.WRLFitStore.drop(INDEX_KEY);
            throw new Error("unknown PDP index schema v" + index.v);
          }
          self.index = index;
          self.render();
        })
        .catch(function (err) {
          // Render nothing at all. This block is an enhancement; a product page that simply does
          // not mention fitment is a normal page, while an error box under the buy button is not.
          self.innerHTML = "";
          self.removeAttribute("data-state");
          self._loading = false;
          /* Retried on the next variant switch, not on re-intersection: re-arming watch() here
             would fire immediately on an element already in view and loop. Capped, because a
             persistent failure must not turn variant-toggling into a request generator. */
          self._failures = (self._failures || 0) + 1;
          if (window.console) console.error("[wrl-fit-check]", err);
        });
    }

    modelsForCurrentSku() {
      var key = lookupKey(this.sku);
      if (!key || !this.index) return [];
      var slots = this.index.skus[key];
      if (!slots) return [];
      var models = this.index.models;
      var out = [];
      for (var i = 0; i < slots.length; i++) {
        var row = models[slots[i]];
        if (row) out.push({ key: row[0], name: row[1] });
      }
      return out;
    }

    /*
     * The shopper's own tractor, if this part actually fits it.
     *
     * The claim is re-derived here, on this page, from this variant's SKU against the live index -
     * the remembered selection supplies only the question ("what about my L3901?"), never the
     * answer. So a model that has been renamed, retired, or dropped from the catalogue since the
     * shopper last used the finder simply fails the `some` below and the badge does not render.
     * Nothing is ever asserted on the strength of what localStorage happens to hold.
     *
     * Model only, and the copy is careful to say so. The PDP index maps SKU to models; it carries
     * no loader dimension, so the loader is deliberately not named here even though the store
     * remembers it. Naming it would be claiming something this page cannot check.
     */
    verifiedHtml(models) {
      if (this.dataset.verified === "false") return "";
      if (!window.WRLFitStore || !window.WRLFitStore.tractor) return "";

      var mine;
      try {
        mine = window.WRLFitStore.tractor();
      } catch (e) {
        return "";
      }
      if (!mine) return "";

      var fits = models.some(function (m) { return m.key === mine.modelKey; });
      if (!fits) return "";

      var label = this.dataset.verifiedLabel || "Verified fit";
      return '<p class="wrl-fit-check__verified">' + CHECK_SVG +
        "<span>" + esc(label) + " for your <b>" + esc(mine.modelName) + "</b></span></p>";
    }

    render() {
      var models = this.modelsForCurrentSku();

      /* A pin, a hose, a coupler: parts that are not fitment parts have no entry, and the right
         answer there is silence. An empty state would invent a question nobody asked. */
      if (!models.length) {
        this.innerHTML = "";
        this.removeAttribute("data-state");
        return;
      }

      var collapsible = models.length > this.inlineMax;
      var open = !collapsible || this.expanded;
      var listId = this.listId;

      var html = this.verifiedHtml(models);

      html +=
        '<div class="wrl-fit-check__head">' +
          '<p class="wrl-fit-check__count">Fits ' + models.length + " tractor model" +
            (models.length === 1 ? "" : "s") + "</p>";

      if (collapsible) {
        html +=
          '<button type="button" class="wrl-fit-check__toggle" aria-expanded="' + open + '"' +
            ' aria-controls="' + esc(listId) + '">' +
            (open ? "Hide the list" : "See the list") +
          "</button>";
      }
      html += "</div>";

      html += '<ul class="wrl-fit-check__models" id="' + esc(listId) + '"' + (open ? "" : " hidden") + ">";
      for (var i = 0; i < models.length; i++) {
        var href = this.pageUrl + "?m=" + encodeURIComponent(slug(models[i].name) + "--" + models[i].key);
        html += '<li><a href="' + esc(href) + '">' + esc(models[i].name) + "</a></li>";
      }
      html += "</ul>";

      this.innerHTML = html;
      this.setAttribute("data-state", "ready");

      if (collapsible) {
        var self = this;
        this.querySelector(".wrl-fit-check__toggle").addEventListener("click", function () {
          self.expanded = !self.expanded;
          self.render();
          var btn = self.querySelector(".wrl-fit-check__toggle");
          if (btn) btn.focus();
        });
      }
    }
  }

  if (!window.customElements.get("wrl-fit-check")) {
    window.customElements.define("wrl-fit-check", WrlFitCheck);
  }
})();
